import { safeReturnTo } from "../logic/return-to";
import type { StartSignInOptions } from "../types";

/**
 * The identity service's routes (RFC 0001 §5–6; 27 §8). Sign-in is a full-page navigation to `/auth/sign-in`, which
 * redirects to AuthKit; everything else is JSON under the API path (VITE_IDENTITY_API_PATH), which the dev server
 * and the gateway forward to the service with the prefix removed. These paths are the service's contract, the same
 * in every environment.
 */
export const AUTH_SIGN_IN = "/auth/sign-in";

export const ROUTES = {
  me: "/me",
  terms: "/me/terms",
  workspaceChoice: "/me/onboarding/workspace",
  profile: "/me/profile",
  security: "/me/security",
  totp: "/me/factors/totp",
  verifyTotp: "/me/factors/totp/verify",
  factor: (id: string) => `/me/factors/${encodeURIComponent(id)}`,
  invitationLink: (token: string) => `/invitations/${encodeURIComponent(token)}`,
  invitationLinkAction: (token: string, action: "accept" | "decline") =>
    `/invitations/${encodeURIComponent(token)}/${action}`,
  sessions: "/me/sessions",
  session: (id: string) => `/me/sessions/${encodeURIComponent(id)}`,
  signOut: "/auth/sign-out",
  switchOrganisation: "/auth/switch-organisation",
  accessRequests: "/access-requests",
  organisation: (id: string) => `/orgs/${encodeURIComponent(id)}`,
  setup: (id: string) => `/orgs/${encodeURIComponent(id)}/setup`,
  members: (id: string) => `/orgs/${encodeURIComponent(id)}/members`,
  invitations: (id: string) => `/orgs/${encodeURIComponent(id)}/invitations`,
  invitation: (id: string, invitationId: string, action: "resend" | "revoke") =>
    `/orgs/${encodeURIComponent(id)}/invitations/${encodeURIComponent(invitationId)}/${action}`,
  member: (id: string, userId: string) =>
    `/orgs/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
  memberAction: (id: string, userId: string, action: "role" | "suspend" | "reactivate") =>
    `/orgs/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}/${action}`,
  policies: (id: string) => `/orgs/${encodeURIComponent(id)}/policies`,
  audit: (id: string, query: string) =>
    `/orgs/${encodeURIComponent(id)}/audit${query === "" ? "" : `?${query}`}`,
  projectAccessRequests: (projectId: string) =>
    `/projects/${encodeURIComponent(projectId)}/access-requests`,
  myProjectAccessRequest: (projectId: string) =>
    `/projects/${encodeURIComponent(projectId)}/access-requests/mine`,
  selfAddToProject: (projectId: string) => `/projects/${encodeURIComponent(projectId)}/self-add`,
  reviewAccessRequests: "/project-access-requests",
  decideAccessRequest: (requestId: string) =>
    `/project-access-requests/${encodeURIComponent(requestId)}/decision`,
} as const;

/** `GET /auth/sign-in?return_to=…`: the address that starts a hosted sign-in (or an invited sign-up). */
export function signInUrl(returnTo: string, options: StartSignInOptions = {}): string {
  const query = new URLSearchParams({ return_to: safeReturnTo(returnTo) });
  if (options.screen !== undefined) query.set("screen", options.screen);
  if (options.invitationToken !== undefined) query.set("invitation_token", options.invitationToken);
  return `${AUTH_SIGN_IN}?${query.toString()}`;
}
